import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { EntityLink } from '../links/EntityLink';
import { prefetchBookkeepingClients } from '../../hooks/useClients';
import { prefetchClientReviews } from '../../hooks/useReviews';
import { useTranslation } from '../../i18n';
import {
  Home24Regular,
  Home24Filled,
  People24Regular,
  People24Filled,
  ClipboardTaskListLtr24Regular,
  ClipboardTaskListLtr24Filled,
  Calendar24Regular,
  Calendar24Filled,
  TaskListLtr24Regular,
  TaskListLtr24Filled,
  PersonAvailable24Regular,
  PersonAvailable24Filled,
  ChartMultiple24Regular,
  ChartMultiple24Filled,
  Settings24Regular,
  Settings24Filled,
  Note24Regular,
  Note24Filled,
  BookInformation24Regular,
  BookInformation24Filled,
  PeopleTeam24Regular,
  PeopleTeam24Filled,
  ArrowTrendingLines24Regular,
  ArrowTrendingLines24Filled,
} from '@fluentui/react-icons';

const navItems = [
  {
    path: '/',
    labelKey: 'nav.dashboard',
    icon: Home24Regular,
    iconFilled: Home24Filled,
  },
  {
    path: '/clients',
    labelKey: 'nav.clients',
    icon: People24Regular,
    iconFilled: People24Filled,
  },
  {
    path: '/reviews',
    labelKey: 'nav.reviews',
    icon: ClipboardTaskListLtr24Regular,
    iconFilled: ClipboardTaskListLtr24Filled,
  },
  {
    path: '/meetings',
    labelKey: 'nav.meetings',
    icon: Calendar24Regular,
    iconFilled: Calendar24Filled,
  },
  {
    path: '/action-items',
    labelKey: 'nav.actionItems',
    icon: TaskListLtr24Regular,
    iconFilled: TaskListLtr24Filled,
  },
  {
    path: '/workload',
    labelKey: 'nav.workload',
    icon: PersonAvailable24Regular,
    iconFilled: PersonAvailable24Filled,
  },
  {
    path: '/productivity',
    labelKey: 'nav.productivity',
    icon: ChartMultiple24Regular,
    iconFilled: ChartMultiple24Filled,
  },
  {
    // Route kept as /task-log so existing links and bookmarks keep working; the page itself is
    // now the bookkeeping Team Performance view (core.WorkTask), not the old Cosmos task log.
    path: '/task-log',
    labelKey: 'nav.teamPerformance',
    icon: ArrowTrendingLines24Regular,
    iconFilled: ArrowTrendingLines24Filled,
  },
  {
    path: '/staff-settings',
    labelKey: 'nav.staffSettings',
    icon: PeopleTeam24Regular,
    iconFilled: PeopleTeam24Filled,
  },
  {
    path: '/settings',
    labelKey: 'nav.settings',
    icon: Settings24Regular,
    iconFilled: Settings24Filled,
  },
  {
    // The staff quick-start (readiness criterion 11, SUP-PROD-10), beside What's new.
    path: '/quick-start',
    labelKey: 'nav.quickStart',
    icon: BookInformation24Regular,
    iconFilled: BookInformation24Filled,
  },
  {
    path: '/patch-notes',
    labelKey: 'nav.whatsNew',
    icon: Note24Regular,
    iconFilled: Note24Filled,
  },
];

// Real links (fleet ruling 2026-10-29): each entry is an <a href>, so right-click → Open in new tab works on the
// menu too. It used to be a Fluent TabList, whose tabs are buttons and can carry no href. The look is the vertical
// tab's: a brand bar on the left of the current page, which is marked aria-current="page".
const useStyles = makeStyles({
  list: { display: 'flex', flexDirection: 'column', listStyle: 'none', margin: 0, padding: 0 },
  link: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: '12px 24px',
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadiusMedium,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorNeutralForeground1 },
    ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' },
  },
  current: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    '::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '8px',
      bottom: '8px',
      width: '3px',
      borderRadius: tokens.borderRadiusCircular,
      backgroundColor: tokens.colorCompoundBrandStroke,
    },
  },
});

export default function NavMenu() {
  const styles = useStyles();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const prefetchFor: Record<string, (() => void) | undefined> = useMemo(() => ({
    '/clients': () => void prefetchBookkeepingClients(queryClient),
    '/reviews': () => void prefetchClientReviews(queryClient),
  }), [queryClient]);

  return (
    <nav
      style={{
        width: '220px',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: '16px 0',
      }}
    >
      <ul className={styles.list}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = isActive ? item.iconFilled : item.icon;
          return (
            <li key={item.path}>
              <EntityLink
                href={item.path}
                className={mergeClasses(styles.link, isActive && styles.current)}
                aria-current={isActive ? 'page' : undefined}
                onMouseEnter={prefetchFor[item.path]}
                onFocus={prefetchFor[item.path]}
              >
                <Icon />
                {t(item.labelKey)}
              </EntityLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
